import { memo, useCallback, useMemo } from 'react';
import { formatNumber, interpolate } from '@/lib/i18n';
import type { VisualizerCopy } from './content';
import type { SceneDefinition } from './scenes';
import { Stage } from './Stage';
import type { StageStatus, StageTexture } from './Stage';
import type { Rotation } from './url-state';
import { useCommittedValue } from './useCommittedValue';

export interface StageSectionProps {
  scene: SceneDefinition;
  status: StageStatus;
  texture: StageTexture | null;
  scale: number;
  rotate: Rotation;
  seams: boolean;
  /** The divider position in the address (0 – 100). */
  committed: number;
  onCommit: (value: number) => void;
  /** The parts of the picture's description that do not depend on the divider. */
  label: { scene: string; stone: string; surface: string; scale: number; rotate: number };
  /** What the "stone" chip says (the stone's name, or that it is loading). */
  stoneChip: string;
  copy: VisualizerCopy;
}

/**
 * The picture and the before / after divider. The divider's *live* position belongs to this component
 * and nothing above it: a drag changes it sixty times a second, and only the picture — whose heavy
 * layers are memoised — needs to hear about that. The address hears about it when the hand is still.
 */
export const StageSection = memo(function StageSection({
  scene,
  status,
  texture,
  scale,
  rotate,
  seams,
  committed,
  onCommit,
  label,
  stoneChip,
  copy,
}: StageSectionProps) {
  const [compare, setCompare] = useCommittedValue(committed, onCommit);
  const onCompare = useCallback(
    (value: number, commit: boolean) => {
      setCompare(value, commit);
    },
    [setCompare],
  );

  const compareText = useCallback(
    (percent: number) =>
      interpolate(copy.compare.value, {
        stone: formatNumber(percent),
        plain: formatNumber(100 - percent),
      }),
    [copy],
  );
  const stageCopy = useMemo(
    () => ({
      compareLabel: copy.compare.label,
      compareText,
      stoneChip,
      plainChip: copy.compare.plainChip,
    }),
    [copy, compareText, stoneChip],
  );

  return (
    <div className="viz-dock">
      <Stage
        scene={scene}
        status={status}
        texture={texture}
        scale={scale}
        rotate={rotate}
        seams={seams}
        compare={compare}
        onCompare={onCompare}
        label={interpolate(copy.stage.label, {
          scene: label.scene,
          stone: label.stone,
          surface: label.surface,
          scale: formatNumber(label.scale),
          rotate: label.rotate,
          share: formatNumber(Math.round(compare)),
        })}
        copy={stageCopy}
      />
    </div>
  );
});
